import universal from 'react-universal-component';

import quizActions from './store/quiz/actions';
import categoryActions from './store/category/actions';
import userActions from './store/user/actions';
import questionActions from './store/question/actions';
import resultActions from './store/result/actions';
import leaderboardActions from './store/leaderboard/actions';
import badgeActions from './store/badge/actions';
import notificationActions from './store/notification/actions';
import extraActions from './store/extra/actions';

import categorySelectors from './store/category/selectors';
import getPreferences from './store/preferences/actions';


const Home = universal(import(`./views/Home`));
const Profile = universal(import(`./views/Profile`));
const Results = universal(import(`./views/Results`));
const Legal = universal(import(`./views/Legal`));
const Question = universal(import(`./views/Question/Question`));
const QuizResults = universal(import(`./views/QuizResults`));
const Leaderboard = universal(import(`./views/Leaderboard`));
const Login = universal(import(`./views/Login`));
const Share = universal(import(`./views/Share`));
const Notifications = universal(import(`./views/Notifications`));
const SingleBadge = universal(import(`./views/SingleBadge`));
const Support = universal(import(`./views/Support`));
const NotFound = universal(import(`./components/NotFound`));
const ShareLanding = universal(import(`./views/ShareLanding`));
const AllBadgesAnonymous = universal(import(`./views/AllBadgesAnonymous`));
const Activation = universal(import(`./views/Activation`));

const routes = [
  {
    path: `/`,
    exact: true,
    component: Home,
    loadData: async (store) => {
      await store.dispatch(categoryActions.getCategories());
      const categories = categorySelectors.all(store.getState());
      store.dispatch(categoryActions.selectCategory({ id: categories[0].id }));
    },
  },
  {
    path: `/profile/:id`,
    component: Profile,
    exact: true,
    private: true,
    loadData: async (store, { params }) => {
      const userId = params.id;
      const authId = store.getState().user.session.authedUserId;
      store.dispatch(userActions.selectUser(userId));
      if (Number(authId) === Number(userId)) {
        await Promise.all([
          store.dispatch(getPreferences()),
          store.dispatch(userActions.getUserPreferences(userId)),
          store.dispatch(userActions.getUserStats(userId)),
          store.dispatch(userActions.getUserBestWorst(userId)),
          store.dispatch(userActions.getUserBadges(userId)),
          store.dispatch(badgeActions.getUserBadgePlacements(userId)),
        ]);
      } else {
        await Promise.all([
          store.dispatch(userActions.getUserStats(userId)),
          store.dispatch(userActions.getUserBestWorst(userId)),
          store.dispatch(userActions.getUserBadges(userId)),
          store.dispatch(badgeActions.getUserBadgePlacements(userId)),
        ]);
      }
    },
  },
  {
    path: `/profile/:id/:content`,
    component: Profile,
    private: true,
    loadData: async (store, { params }) => {
      const userId = params.id;
      const authId = store.getState().user.session.authedUserId;
      store.dispatch(userActions.selectUser(userId));
      if (Number(authId) === Number(userId)) {
        await Promise.all([
          store.dispatch(getPreferences()),
          store.dispatch(userActions.getUserPreferences(userId)),
          store.dispatch(userActions.getUserStats(userId)),
          store.dispatch(userActions.getUserBestWorst(userId)),
          store.dispatch(userActions.getUserBadges(userId)),
          store.dispatch(badgeActions.getUserBadgePlacements(userId)),
        ]);
      } else {
        await Promise.all([
          store.dispatch(userActions.getUserStats(userId)),
          store.dispatch(userActions.getUserBestWorst(userId)),
          store.dispatch(userActions.getUserBadges(userId)),
          store.dispatch(badgeActions.getUserBadgePlacements(userId)),
        ]);
      }
    },
  },
  {
    path: `/question/:id/results`,
    component: Results,
    loadData: async (store, match, req) => { // eslint-disable-line
      const result = await store.dispatch(resultActions.getResult(match.params.id));
      store.dispatch(resultActions.selectResult(match.params.id));
      if (!result) {
        return;
      }
      if (req.cookies.wall && !req.query.wall) {
        const queryParams = req.query;
        queryParams.wall = req.cookies.wall;
        store.dispatch(extraActions.saveQueryParams(queryParams));
      }
      if (!store.getState().user.JWT) {
        store.dispatch(resultActions.getPlayedAnswer(result.question_id, req.cookies));
      }
      store.dispatch(quizActions.selectQuiz(result.quiz));
      const previousPlayed = JSON.parse(req.cookies.playedAnswer || `{}`);

      const response = await store.dispatch(quizActions.getQuizResults(result.quiz));
      const hasAttempted = response.data.attempted && response.data.attempted > 0;
      await store.dispatch(quizActions.getQuiz(result.quiz, hasAttempted));

      await store.dispatch(questionActions.getQuestion(match.params.id));
      await store.dispatch(questionActions.selectNextQuestion(match.params.id));
      if (previousPlayed && previousPlayed.question === parseInt(match.params.id, 10)) {
        store.dispatch(quizActions.removeQuizQuestion(previousPlayed.question));
      }
    },
  },
  {
    path: `/question/:id`,
    component: Question,
    loadData: async (store, match, req) => {
      const [id] = match.params.id.split(`?`);
      let question = null;
      try {
        question = await store.dispatch(questionActions.getQuestion(id));
      } catch (e) {
        return;
      }
      store.dispatch(questionActions.selectQuestion(id));
      await store.dispatch(quizActions.selectQuiz(question.quiz));

      const response = await store.dispatch(quizActions.getQuizResults(question.quiz));
      const hasAttempted = response.data.attempted && response.data.attempted > 0;
      await store.dispatch(quizActions.getQuiz(question.quiz, hasAttempted));

      if (req.query.answerId) {
        store.dispatch(quizActions.removeQuizQuestion(question.id));
      }
      store.dispatch(questionActions.selectNextQuestion(id));
    },
  },
  {
    path: `/quiz/:id/results`,
    component: QuizResults,
    loadData: async (store, { params }, req) => {
      if (req.cookies.quiz_wall && !req.query.quiz_wall) {
        const queryParams = req.query;
        queryParams.quiz_wall = req.cookies.quiz_wall;
        store.dispatch(extraActions.saveQueryParams(queryParams));
      }
      const response = await store.dispatch(quizActions.getQuizResults(params.id));
      const hasAttempted = response.data.attempted && response.data.attempted > 0;
      const quiz = await store.dispatch(quizActions.getQuiz(params.id, hasAttempted));
      await store.dispatch(categoryActions.selectCategory({ id: quiz.category }));
      await store.dispatch(categoryActions.getCategory({ id: quiz.category }));
    },
  },
  {
    path: `/support`,
    component: Support,
  },
  {
    path: `/leaderboard/:board`,
    component: Profile,
    private: true,
    loadData: async (store, { params }) => { // eslint-disable-line
      const [board] = params.board.split(`?`);
      const userId = store.getState().user.session.authedUserId;
      await store.dispatch(leaderboardActions.selectLeaderboard(board || `top100`));
      if (userId) {
        store.dispatch(userActions.selectUser(userId));
        await Promise.all([
          store.dispatch(userActions.getUserStats(userId)),
          store.dispatch(userActions.getUserBestWorst(userId)),
        ]);
      }
    },
  },
  {
    path: `/leaderboard`,
    exact: true,
    component: Leaderboard,
    loadData: async (store) => {
      store.dispatch(leaderboardActions.selectLeaderboard(`top100`));
    },
  },

  {
    path: `/login`,
    component: Login,
  },
  {
    path: `/share`,
    component: Share,
  },
  {
    path: `/referral/:username`,
    component: ShareLanding,
    loadData: async (store) => {
      await store.dispatch(quizActions.getQuizzes());
    },
  },
  {
    path: `/referral`,
    component: ShareLanding,
    loadData: async (store) => {
      await store.dispatch(quizActions.getQuizzes());
    },
  },
  {
    path: `/notifications`,
    component: Notifications,
    private: true,
    loadData: async (store) => {
      await store.dispatch(notificationActions.getNotifications());
    },
  },
  {
    path: `/badges/:slug`,
    component: SingleBadge,
    loadData: (store, { params }) => {
      return new Promise((resolve, reject) => {
        const [slug] = params.slug.split(`?`);
        store.dispatch(badgeActions.selectBadge(slug));
        store.dispatch(badgeActions.getBadge(slug))
          .then(() => {
            resolve();
          })
          .catch(() => {
            reject();
          });
      });
    },
  },
  {
    path: `/badges`,
    component: AllBadgesAnonymous,
    exact: true,
    loadData: () => {
      return new Promise((resolve) => {
        resolve();
      });
    },
  },
  {
    path: `/activation/:id/:token`,
    component: Activation,
  },
  {
    path: `/calpolicy`,
    component: Legal,
    exact: true,
  },
  {
    path: `/cookie`,
    component: Legal,
    exact: true,
  },
  {
    path: `/terms`,
    component: Legal,
    exact: true,
  },
  {
    path: `/privacy`,
    component: Legal,
    exact: true,
  },
  {
    path: ``,
    component: NotFound,
  },
];

export default routes;
